import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import "./../NcdForm.css";
import Loader from "../../../../components/Loader";
import { ncdAPI } from "../../../../services/ncd.api";
import { formatToApiDate, formatToInputDate } from "../../../../helpers/common-helper";
import {
    Button,
    FormControl,
    Select,
    MenuItem,
    Box,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    IconButton,
    FormHelperText,
    Checkbox,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Import Tab Subcomponents
import { OralExaminationTab } from "./OralExaminationTab";
import { OralDiagnosisTab } from "./OralDiagnosisTab";
import { OralReviewTab } from "./OralReviewTab";
import { OralFollowupTab } from "./OralFollowupTab";
import { OralReferTab } from "./OralReferTab";
import { FACILITY_MAP, getFacilityLabel } from "../../utils/constants";

dayjs.extend(customParseFormat);

export const OralScreening = ({ individualId, oralScreeningId, viewMode }) => {
    const today = new Date().toISOString().split("T")[0];

    const [isPageLoading, setIsPageLoading] = useState(false);
    const [oralData, setOralData] = useState(null);

    // Tab workflows (0: Examination, 1: Diagnosis, 2: Review, 3: Followup, 4: Refer)
    const [activeWorkflowTab, setActiveWorkflowTab] = useState(0);

    // Modal states for updating screening status
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [modalDate, setModalDate] = useState(today);
    const [modalStatus, setModalStatus] = useState("");
    const [modalFacility, setModalFacility] = useState("");
    const [modalVerified, setModalVerified] = useState(false);
    const [modalErrors, setModalErrors] = useState({});
    const [screeningStatus, setScreeningStatus] = useState("");
    const [followupHistory, setFollowupHistory] = useState([]);
    const [medicines, setMedicines] = useState([
        { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }
    ]);
    const [medicineOptions, setMedicineOptions] = useState([]);
    const [medicineSearchText, setMedicineSearchText] = useState('');
    const [treatmentType, setTreatmentType] = useState('');
    const [treatmentOther, setTreatmentOther] = useState('');
    const [encounterId, setEncounterId] = useState(null);
    const [isDiagnosisSaved, setIsDiagnosisSaved] = useState(false);

    const statusOptions = [
        { value: "Treatement", label: "Treatement" },
        { value: "Diagnosed", label: "Diagnosed" }
    ];

    const facilityOptions = [
        { value: "CHC", label: "CHC" },
        { value: "DH", label: "DH" },
        { value: "Tertiary", label: "Tertiary" }
    ];

    const validateModalForm = () => {
        const errors = {};
        if (!modalDate) errors.date = "Date is required";
        if (!modalStatus) errors.status = "Status is required";
        if (!modalFacility) errors.facility = "Facility is required";
        setModalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveModalStatus = async () => {
        if (!validateModalForm()) {
            toast.error("Please fill in all required fields.");
            return;
        }

        const payload = {
            changeFacility: modalFacility,
            changeStatus: modalStatus,
            statusVerified: modalVerified,
            consultationDate: formatToApiDate(modalDate)
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.updateSreeningStatus(100, individualId, oralScreeningId, payload);
            if (response && response.success) {
                setScreeningStatus(modalStatus);
                toast.success(`Screening status updated to "${modalStatus}" successfully!`);
                setIsStatusModalOpen(false);
            } else {
                toast.error(response?.message || "Failed to update screening status");
            }
        } catch (error) {
            console.error("updateSreeningStatus error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while updating screening status");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleOpenUpdateModal = () => {
        setModalDate(formData.assessmentDate || today);
        setModalStatus(screeningStatus);
        setModalFacility("");
        setModalVerified(false);
        setModalErrors({});
        setIsStatusModalOpen(true);
    };

    // Diagnosis form fields
    const [diagnosisAction, setDiagnosisAction] = useState("NAD");
    const [doctorsObservation, setDoctorsObservation] = useState("");
    const [isNadModalOpen, setIsNadModalOpen] = useState(false);
    const [isNad, setIsNad] = useState(false);
    const [referralRecommended, setReferralRecommended] = useState(false);
    const [managementRecommended, setManagementRecommended] = useState(false);
    const [isDiagnosed, setIsDiagnosed] = useState(false);
    const [patientDiagnosis, setPatientDiagnosis] = useState("");
    const [diagnosisRecommendationText, setDiagnosisRecommendationText] = useState("");
    const [checkForRegression, setCheckForRegression] = useState(false);
    const [isRegressionModalOpen, setIsRegressionModalOpen] = useState(false);
    const [lesionStatus, setLesionStatus] = useState("regression");
    const [regressionObserved, setRegressionObserved] = useState(null);
    const [actionTakenTitle, setActionTakenTitle] = useState("Action to be taken");
    const [actionTakenText, setActionTakenText] = useState("");
    const [isActionAlertDialogOpen, setIsActionAlertDialogOpen] = useState(false);
    const [isCancerConfirmDialogOpen, setIsCancerConfirmDialogOpen] = useState(false);
    const [isCancerConfirmedLocked, setIsCancerConfirmedLocked] = useState(false);
    const [pendingDiagnosisPayload, setPendingDiagnosisPayload] = useState(null);

    // Review / Follow Up form fields
    const [reviewObservations, setReviewObservations] = useState("");
    const [referralRemarks, setReferralRemarks] = useState("");

    // Follow Up form fields
    const [followupReason, setFollowupReason] = useState("");
    const [followupTo, setFollowupTo] = useState("");
    const [durationDays, setDurationDays] = useState("0");
    const [durationWeeks, setDurationWeeks] = useState("0");
    const [durationMonths, setDurationMonths] = useState("0");
    const [dueDate, setDueDate] = useState("");
    const [errors, setErrors] = useState({});

    // DH / CHC Diagnosis state & facility check
    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")) || {};
    const facilityType = loggedData.facilityType || (loggedData.facilityTypeId === 3400 ? "DH" : loggedData.facilityTypeId === 3300 ? "CHC" : "PHC");
    const isDhOrChc = facilityType === "DH" || facilityType === "CHC" || loggedData.facilityTypeId === 3400 || loggedData.facilityTypeId === 3300;

    const [dhDiagnosisData, setDhDiagnosisData] = useState({
        typeOfDiagnosis: '',
        noAbnormalityDetected: false,
        normalVariation: '',
        normalVariationRemarks: '',
        frictionalKeratosis: '',
        frictionalKeratosisRemarks: '',
        tobaccoPouchLesion: '',
        tobaccoPouchLesionRemarks: '',
        smokersPalate: '',
        smokersPalateRemarks: '',
        oralLichenoidReaction: '',
        oralLichenoidReactionRemarks: '',
        proliferativeVerrucousLeukoplakia: '',
        proliferativeVerrucousLeukoplakiaRemarks: '',
        oralLichenPlanus: '',
        oralSubmucousFibrosis: '',
        oralLeukoplakia: '',
        oralCandidiasis: '',
        benignUlcer: '',
        benignGrowth: '',
        malignantUlcer: '',
        malignantUlcerRemarks: '',
        malignantGrowth: '',
        malignantGrowthRemarks: '',
        anyOther: '',
        anyOtherRemarks: ''
    });

    const [dhErrors, setDhErrors] = useState({});

    const getPatientDiagnosisString = (diag) => {
        if (!diag) return "";
        const parts = [];
        if (diag.normalVariation) parts.push("Normal Variation");
        if (diag.keratosis) parts.push("Frictional keratosis");
        if (diag.tobaccoPouchLesion) parts.push("Tobacco Pouch Lesion");
        if (diag.smokersPalate) parts.push("Smokers Palate");
        if (diag.lichenoidReaction) parts.push("Oral Lichenoid Reaction");
        if (diag.proliferativeLeukoplakia) parts.push("Proliferative Verrucous Leukoplakia");
        if (diag.lichenPlanus) parts.push("Lichen Planus");
        if (diag.submucousFibrosis) parts.push("Submucous fibrosis");
        if (diag.leukoplakia) parts.push("Leukoplakia");
        if (diag.candidiasis) parts.push("Candidiasis");
        if (diag.benignUlcer) parts.push("Benign Ulcer");
        if (diag.benignGrowth) parts.push("Benign Growth");
        if (diag.malignantUlcer) parts.push("Malignant Ulcer");
        if (diag.malignantGrowth) parts.push("Malignant Growth");
        if (diag.other) parts.push("Any Other");
        return parts.join("  |  ");
    };

    useEffect(() => {
        const fetchDiagnosisTabDetails = async () => {
            if (activeWorkflowTab === 1 && individualId && oralScreeningId) {
                try {
                    setIsPageLoading(true);
                    const [diagResponse, respaction] = await Promise.all([
                        ncdAPI.getScreeningDiagnosis(100, individualId, oralScreeningId).catch(err => {
                            console.error("getScreeningDiagnosis error:", err);
                            return null;
                        }),
                        ncdAPI.actionDetails(100, individualId, oralScreeningId).catch(err => {
                            console.error("actionDetails error:", err);
                            return null;
                        })
                    ]);

                    if (respaction?.data?.curActName) {
                        setScreeningStatus(respaction.data.curActName);
                    }

                    if (diagResponse && (diagResponse.success || diagResponse.data || diagResponse.diagnosis)) {
                        const diagData = diagResponse.data || diagResponse;
                        const diagnosed = !!diagData.isDiagnosed;
                        setIsDiagnosed(diagnosed);

                        const isNadFlag = !!diagData.isNad;
                        const referralRecFlag = !!diagData.referralRecommended;
                        const managementRecFlag = !!diagData.managementRecommended;

                        setIsNad(isNadFlag);
                        setReferralRecommended(referralRecFlag);
                        setManagementRecommended(managementRecFlag);

                        if (diagData.diagnosisRecommendationText) {
                            setDiagnosisRecommendationText(diagData.diagnosisRecommendationText);
                            setActionTakenTitle("Action to be taken");
                            setActionTakenText(diagData.diagnosisRecommendationText);
                            setIsActionAlertDialogOpen(true);
                        }

                        if (!isDhOrChc) {
                            if (!diagnosed) {
                                if (diagData.checkForRegression) {
                                    setCheckForRegression(true);
                                    setIsRegressionModalOpen(true);
                                } else {
                                    if (isNadFlag) {
                                        setDiagnosisAction("NAD");
                                    } else if (referralRecFlag) {
                                        setDiagnosisAction("Refer");
                                    } else if (managementRecFlag) {
                                        setDiagnosisAction("Management");
                                    }
                                }
                            } else {
                                setDiagnosisAction("FollowUp");
                                if (diagData.diagnosis) {
                                    setPatientDiagnosis(getPatientDiagnosisString(diagData.diagnosis));
                                }
                            }
                        } else {
                            if (diagData.diagnosis) {
                                const diag = diagData.diagnosis;
                                const isProv = diag.provisionalDiagnosis === true;
                                const isCancerConfirmed = diag.isCancerConfirmed || (diag.malignantUlcer || diag.malignantGrowth);

                                if (isCancerConfirmed && !isProv) {
                                    setIsCancerConfirmedLocked(true);
                                }

                                setDhDiagnosisData({
                                    typeOfDiagnosis: isProv ? "Provisional" : (diag.provisionalDiagnosis === false ? "Confirmed" : ""),
                                    noAbnormalityDetected: !!diag.isNAD,
                                    normalVariation: diag.normalVariation === true ? "Yes" : diag.normalVariation === false ? "No" : "",
                                    normalVariationRemarks: diag.normalVariationRemarks || "",
                                    frictionalKeratosis: diag.keratosis === true ? "Yes" : diag.keratosis === false ? "No" : "",
                                    frictionalKeratosisRemarks: diag.keratosisRemarks || "",
                                    tobaccoPouchLesion: diag.tobaccoPouchLesion === true ? "Yes" : diag.tobaccoPouchLesion === false ? "No" : "",
                                    tobaccoPouchLesionRemarks: diag.tobaccoPouchLesionRemarks || "",
                                    smokersPalate: diag.smokersPalate === true ? "Yes" : diag.smokersPalate === false ? "No" : "",
                                    smokersPalateRemarks: diag.smokersPalateRemarks || "",
                                    oralLichenoidReaction: diag.lichenoidReaction === true ? "Yes" : diag.lichenoidReaction === false ? "No" : "",
                                    oralLichenoidReactionRemarks: diag.lichenoidReactionRemarks || "",
                                    proliferativeVerrucousLeukoplakia: diag.proliferativeLeukoplakia === true ? "Yes" : diag.proliferativeLeukoplakia === false ? "No" : "",
                                    proliferativeVerrucousLeukoplakiaRemarks: diag.proliferativeLeukoplakiaRemarks || "",
                                    oralLichenPlanus: diag.lichenPlanus || "",
                                    oralSubmucousFibrosis: diag.submucousFibrosis || "",
                                    oralLeukoplakia: diag.leukoplakia || "",
                                    oralCandidiasis: diag.candidiasis || "",
                                    benignUlcer: diag.benignUlcer || "",
                                    benignGrowth: diag.benignGrowth || "",
                                    malignantUlcer: diag.malignantUlcer === true ? "Yes" : diag.malignantUlcer === false ? "No" : "",
                                    malignantUlcerRemarks: diag.malignantUlcerRemarks || "",
                                    malignantGrowth: diag.malignantGrowth === true ? "Yes" : diag.malignantGrowth === false ? "No" : "",
                                    malignantGrowthRemarks: diag.malignantGrowthRemarks || "",
                                    anyOther: diag.other === true ? "Yes" : diag.other === false ? "No" : "",
                                    anyOtherRemarks: diag.otherRemarks || ""
                                });

                                if (diagnosed) {
                                    setPatientDiagnosis(getPatientDiagnosisString(diag));
                                }
                            }
                        }

                        if (diagData.docObservations) {
                            setDoctorsObservation(diagData.docObservations);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching diagnosis tab details:", error);
                } finally {
                    setIsPageLoading(false);
                }
            }
        };

        fetchDiagnosisTabDetails();
    }, [activeWorkflowTab, isDhOrChc, individualId, oralScreeningId]);

    // Calculate next visit date based on days/weeks/months
    const calculateDueDate = (days, weeks, months) => {
        const baseDateStr = formData?.assessmentDate || oralData?.data?.exam?.assessmentDate;
        let date = baseDateStr ? dayjs(baseDateStr, ['DD-MM-YYYY', 'YYYY-MM-DD']) : dayjs();
        if (!date.isValid()) date = dayjs();
        if (days && !isNaN(days)) date = date.add(parseInt(days), 'day');
        if (weeks && !isNaN(weeks)) date = date.add(parseInt(weeks) * 7, 'day');
        if (months && !isNaN(months)) date = date.add(parseInt(months), 'month');
        return date.format('DD-MM-YYYY');
    };

    const handleDurationChange = (type, val) => {
        let days = "0";
        let weeks = "0";
        let months = "0";
        if (type === 'days') {
            days = val;
            setDurationDays(val);
            setDurationWeeks("0");
            setDurationMonths("0");
        } else if (type === 'weeks') {
            weeks = val;
            setDurationWeeks(val);
            setDurationDays("0");
            setDurationMonths("0");
        } else if (type === 'months') {
            months = val;
            setDurationMonths(val);
            setDurationDays("0");
            setDurationWeeks("0");
        }

        if (errors.duration || errors[`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]) {
            setErrors(prev => ({
                ...prev,
                duration: null,
                [`duration${type.charAt(0).toUpperCase() + type.slice(1)}`]: null
            }));
        }

        const d = parseInt(days) || 0;
        const w = parseInt(weeks) || 0;
        const m = parseInt(months) || 0;

        if (d === 0 && w === 0 && m === 0) {
            setDueDate("");
        } else {
            const calculated = calculateDueDate(days, weeks, months);
            setDueDate(calculated);
            if (errors.dueDate) {
                setErrors(prev => ({ ...prev, dueDate: null }));
            }
        }
    };

    const validateFollowupForm = () => {
        const newErrors = {};
        if (!followupReason || !followupReason.trim()) {
            newErrors.followupReason = "Reason for Follow-up is required";
        }
        if (!followupTo || followupTo === "" || followupTo === "None") {
            newErrors.followupTo = "Follow-up facility is required";
        }

        const days = parseInt(durationDays) || 0;
        const weeks = parseInt(durationWeeks) || 0;
        const months = parseInt(durationMonths) || 0;

        if (days < 0) newErrors.durationDays = "Days cannot be negative";
        if (weeks < 0) newErrors.durationWeeks = "Weeks cannot be negative";
        if (months < 0) newErrors.durationMonths = "Months cannot be negative";

        if (days === 0 && weeks === 0 && months === 0) {
            newErrors.duration = "At least one duration (days/weeks/months) is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateReferralForm = () => {
        const newErrors = {};
        if (!referTo || referTo === "") {
            newErrors.referTo = "Referred To is required";
        }
        if (!referReason || referReason === "") {
            newErrors.referReason = "Reason for Referral is required";
        }
        if (!referredSpecialty || referredSpecialty === "") {
            newErrors.referredSpecialty = "Referred Specialty is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Refer form fields
    const [referDate, setReferDate] = useState(today);
    const [referTo, setReferTo] = useState("");
    const [referReason, setReferReason] = useState("");
    const [provisionalDiagnosis, setProvisionalDiagnosis] = useState("");
    const [referredSpecialty, setReferredSpecialty] = useState("");
    const [seekingPrivateCare, setSeekingPrivateCare] = useState("");

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    // Mouth overlay coordinate visualizer variables
    const [activeDots, setActiveDots] = useState([]);
    const wrapperRef = useRef();
    const dots = Array.from({ length: 16 }, (_, i) => ({
        id: `dot${i + 1}`,
        value: `OP${i + 1}`,
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsPageLoading(true);
                const responseData = await ncdAPI.getOralScreening(individualId, oralScreeningId);
                setOralData(responseData);

                const hasDiag = responseData?.optedToReview || responseData?.optedToRefer || responseData?.optedForManagement || responseData?.diagnosis || responseData?.data?.optedToReview || responseData?.data?.optedToRefer || responseData?.data?.optedForManagement || responseData?.data?.diagnosis;
                if (hasDiag) {
                    setIsDiagnosisSaved(true);
                }

                try {
                    const diagResponse = await ncdAPI.getScreeningDiagnosis(100, individualId, oralScreeningId);
                    if (diagResponse && (diagResponse.success || diagResponse.data || diagResponse.diagnosis)) {
                        const diagData = diagResponse.data || diagResponse;
                        if (diagData.diagnosis?.isNAD) {
                            setDiagnosisAction("NAD");
                        } else if (diagData.optedToRefer) {
                            setDiagnosisAction("Refer");
                        } else if (diagData.optedToReview) {
                            setDiagnosisAction("Review");
                        } else if (diagData.optedForManagement) {
                            setDiagnosisAction("Management");
                        }

                        if (diagData.docObservations) {
                            setDoctorsObservation(diagData.docObservations);
                        }

                        if (diagData.diagnosis) {
                            const diag = diagData.diagnosis;
                            setDhDiagnosisData({
                                typeOfDiagnosis: diag.provisionalDiagnosis === true ? "Provisional" : (diag.provisionalDiagnosis === false ? "Confirmed" : ""),
                                noAbnormalityDetected: !!diag.isNAD,
                                normalVariation: diag.normalVariation === true ? "Yes" : diag.normalVariation === false ? "No" : "",
                                normalVariationRemarks: diag.normalVariationRemarks || "",
                                frictionalKeratosis: diag.keratosis === true ? "Yes" : diag.keratosis === false ? "No" : "",
                                frictionalKeratosisRemarks: diag.keratosisRemarks || "",
                                tobaccoPouchLesion: diag.tobaccoPouchLesion === true ? "Yes" : diag.tobaccoPouchLesion === false ? "No" : "",
                                tobaccoPouchLesionRemarks: diag.tobaccoPouchLesionRemarks || "",
                                smokersPalate: diag.smokersPalate === true ? "Yes" : diag.smokersPalate === false ? "No" : "",
                                smokersPalateRemarks: diag.smokersPalateRemarks || "",
                                oralLichenoidReaction: diag.lichenoidReaction === true ? "Yes" : diag.lichenoidReaction === false ? "No" : "",
                                oralLichenoidReactionRemarks: diag.lichenoidReactionRemarks || "",
                                proliferativeVerrucousLeukoplakia: diag.proliferativeLeukoplakia === true ? "Yes" : diag.proliferativeLeukoplakia === false ? "No" : "",
                                proliferativeVerrucousLeukoplakiaRemarks: diag.proliferativeLeukoplakiaRemarks || "",
                                oralLichenPlanus: diag.lichenPlanus || "",
                                oralSubmucousFibrosis: diag.submucousFibrosis || "",
                                oralLeukoplakia: diag.leukoplakia || "",
                                oralCandidiasis: diag.candidiasis || "",
                                benignUlcer: diag.benignUlcer || "",
                                benignGrowth: diag.benignGrowth || "",
                                malignantUlcer: diag.malignantUlcer === true ? "Yes" : diag.malignantUlcer === false ? "No" : "",
                                malignantUlcerRemarks: diag.malignantUlcerRemarks || "",
                                malignantGrowth: diag.malignantGrowth === true ? "Yes" : diag.malignantGrowth === false ? "No" : "",
                                malignantGrowthRemarks: diag.malignantGrowthRemarks || "",
                                anyOther: diag.other === true ? "Yes" : diag.other === false ? "No" : "",
                                anyOtherRemarks: diag.otherRemarks || ""
                            });
                        }
                    }
                } catch (diagErr) {
                    console.error("Failed to fetch screening diagnosis in fetchData:", diagErr);
                }

                if (responseData && responseData.success && responseData.data) {
                    const fetchedData = responseData.data;
                    if (fetchedData.diagnosis?.isNAD) {
                        setDiagnosisAction("NAD");
                    } else if (fetchedData.optedToRefer) {
                        setDiagnosisAction("Refer");
                    } else if (fetchedData.optedToReview) {
                        setDiagnosisAction("Review");
                    } else if (fetchedData.optedForManagement) {
                        setDiagnosisAction("Management");
                    }

                    if (fetchedData.diagnosis) {
                        const diag = fetchedData.diagnosis;
                        setDhDiagnosisData({
                            typeOfDiagnosis: diag.provisionalDiagnosis === true ? "Provisional" : (diag.provisionalDiagnosis === false ? "Confirmed" : ""),
                            noAbnormalityDetected: !!diag.isNAD,
                            normalVariation: diag.normalVariation === true ? "Yes" : diag.normalVariation === false ? "No" : "",
                            normalVariationRemarks: diag.normalVariationRemarks || "",
                            frictionalKeratosis: diag.keratosis === true ? "Yes" : diag.keratosis === false ? "No" : "",
                            frictionalKeratosisRemarks: diag.keratosisRemarks || "",
                            tobaccoPouchLesion: diag.tobaccoPouchLesion === true ? "Yes" : diag.tobaccoPouchLesion === false ? "No" : "",
                            tobaccoPouchLesionRemarks: diag.tobaccoPouchLesionRemarks || "",
                            smokersPalate: diag.smokersPalate === true ? "Yes" : diag.smokersPalate === false ? "No" : "",
                            smokersPalateRemarks: diag.smokersPalateRemarks || "",
                            oralLichenoidReaction: diag.lichenoidReaction === true ? "Yes" : diag.lichenoidReaction === false ? "No" : "",
                            oralLichenoidReactionRemarks: diag.lichenoidReactionRemarks || "",
                            proliferativeVerrucousLeukoplakia: diag.proliferativeLeukoplakia === true ? "Yes" : diag.proliferativeLeukoplakia === false ? "No" : "",
                            proliferativeVerrucousLeukoplakiaRemarks: diag.proliferativeLeukoplakiaRemarks || "",
                            oralLichenPlanus: diag.lichenPlanus || "",
                            oralSubmucousFibrosis: diag.submucousFibrosis || "",
                            oralLeukoplakia: diag.leukoplakia || "",
                            oralCandidiasis: diag.candidiasis || "",
                            benignUlcer: diag.benignUlcer || "",
                            benignGrowth: diag.benignGrowth || "",
                            malignantUlcer: diag.malignantUlcer === true ? "Yes" : diag.malignantUlcer === false ? "No" : "",
                            malignantUlcerRemarks: diag.malignantUlcerRemarks || "",
                            malignantGrowth: diag.malignantGrowth === true ? "Yes" : diag.malignantGrowth === false ? "No" : "",
                            malignantGrowthRemarks: diag.malignantGrowthRemarks || "",
                            anyOther: diag.other === true ? "Yes" : diag.other === false ? "No" : "",
                            anyOtherRemarks: diag.otherRemarks || ""
                        });
                    }
                    if (fetchedData.docObservations) {
                        setDoctorsObservation(fetchedData.docObservations);
                    }

                    const respaction = await ncdAPI.actionDetails(
                        100,
                        individualId,
                        oralScreeningId
                    );

                    if (respaction?.data?.curActName) {
                        setScreeningStatus(respaction?.data?.curActName);
                    }

                    const formattedDate = formatToInputDate(fetchedData.assessmentDate) || today;

                    const newFormData = {
                        assessmentDate: formattedDate,
                        exam: {
                            abnormalPatches: fetchedData.exam?.abnormalPatches ?? false,
                            noAbnormalityFound: fetchedData.exam?.noAbnormalityFound ?? false,
                            examDate: formatToInputDate(fetchedData.exam?.examDate) || '',
                            cavity: fetchedData.exam?.cavity || '',
                            chronicTrauma: fetchedData.exam?.chronicTrauma || '',
                            dentalCaries: fetchedData.exam?.dentalCaries ?? null,
                            dentures: fetchedData.exam?.dentures ?? null,
                            lossOfTooth: fetchedData.exam?.lossOfTooth ?? null,
                            fissures: fetchedData.exam?.fissures ?? null,
                            induration: fetchedData.exam?.induration ?? null,
                            lymphadenopathy: fetchedData.exam?.lymphadenopathy ?? null,
                            otherFindings: fetchedData.exam?.otherFindings ?? null,
                            otherFindingsTxt: fetchedData.exam?.otherFindingsTxt || '',
                            rootCanalHistory: fetchedData.exam?.rootCanalHistory || '',
                            site: fetchedData.exam?.site || '',
                            visualExam: {
                                leukoplakiaPatch: fetchedData.exam?.visualExam?.leukoplakiaPatch || [],
                                erythroplakiaPatch: fetchedData.exam?.visualExam?.erythroplakiaPatch || [],
                                ulcerPatch: fetchedData.exam?.visualExam?.ulcerPatch || [],
                                growthPatch: fetchedData.exam?.visualExam?.growthPatch || [],
                                lockedjawPatch: fetchedData.exam?.visualExam?.lockedjawPatch || [],
                                subfibrosisPatch: fetchedData.exam?.visualExam?.subfibrosisPatch || [],
                                smokerspalatePatch: fetchedData.exam?.visualExam?.smokerspalatePatch || [],
                                lichenPlanusPatch: fetchedData.exam?.visualExam?.lichenPlanusPatch || []
                            }
                        }
                    };

                    setFormData(newFormData);

                    const patchTypes = {
                        leukoplakiaPatch: 'Leukoplakia',
                        erythroplakiaPatch: 'Erythroplakia',
                        ulcerPatch: 'Ulcer',
                        growthPatch: 'Growth',
                        lockedjawPatch: 'LockedJaw',
                        subfibrosisPatch: 'Submucousfibrosis',
                        smokerspalatePatch: 'Smokerspalate',
                        lichenPlanusPatch: 'LichenPlanus',
                    };

                    const dotToPatch = {};
                    let active = [];

                    for (const patch in patchTypes) {
                        const dots = fetchedData.exam?.visualExam?.[patch];
                        if (Array.isArray(dots)) {
                            dots.forEach((dot) => {
                                dotToPatch[dot] = patchTypes[patch];
                                if (!active.includes(dot)) {
                                    active.push(dot);
                                }
                            });
                        }
                    }

                    setActiveDots(active);
                    const fetchedVisualExam = {
                        leukoplakiaPatch: fetchedData.exam?.visualExam?.leukoplakiaPatch || [],
                        erythroplakiaPatch: fetchedData.exam?.visualExam?.erythroplakiaPatch || [],
                        ulcerPatch: fetchedData.exam?.visualExam?.ulcerPatch || [],
                        growthPatch: fetchedData.exam?.visualExam?.growthPatch || [],
                        lockedjawPatch: fetchedData.exam?.visualExam?.lockedjawPatch || [],
                        subfibrosisPatch: fetchedData.exam?.visualExam?.subfibrosisPatch || [],
                        smokerspalatePatch: fetchedData.exam?.visualExam?.smokerspalatePatch || [],
                        lichenPlanusPatch: fetchedData.exam?.visualExam?.lichenPlanusPatch || []
                    };
                    setSavedVisualExam(fetchedVisualExam);
                    setSelectedValue('');
                }

                // Fetch follow-up history
                try {
                    const historyResponse = await ncdAPI.getScreeningFollowup(100, individualId, oralScreeningId);
                    if (historyResponse) {
                        setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
                    }
                } catch (error) {
                    console.error("Failed to fetch follow-up history:", error);
                }

                // Fetch screening treatment to load existing medicines
                try {
                    const treatmentResponse = await ncdAPI.getScreeningTreatment(100, individualId, oralScreeningId);
                    if (treatmentResponse && treatmentResponse.success && treatmentResponse.data) {
                        const medicalTreatment = treatmentResponse.data.treatment && Array.isArray(treatmentResponse.data.treatment)
                            ? treatmentResponse.data.treatment.find(t => Array.isArray(t.medicineMangementMedList) && t.medicineMangementMedList.length > 0)
                            : null;
                        const savedMeds = (treatmentResponse.data.medicines && treatmentResponse.data.medicines.length > 0)
                            ? treatmentResponse.data.medicines
                            : (medicalTreatment ? medicalTreatment.medicineMangementMedList : []);

                        if (savedMeds && savedMeds.length > 0) {
                            setMedicines(savedMeds.map(med => ({
                                medicine: med.name || med.drug || '',
                                frequency: med.freq || med.frequency || '',
                                duration: med.dur !== undefined && med.dur !== null ? String(med.dur) : (med.durationDays || med.duration || ''),
                                quantity: med.quan !== undefined && med.quan !== null ? String(med.quan) : (med.quantity || ''),
                                instruction: med.spInst || med.specialInstruction || med.instruction || ''
                            })));
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch screening treatment:", error);
                }

                setIsPageLoading(false);
            } catch (err) {
                setIsPageLoading(false);
                console.error("Failed to fetch and populate form data", err);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individualId, oralScreeningId]);

    useEffect(() => {
        const fetchMedicineOptions = async () => {
            if (medicineSearchText && medicineSearchText.length >= 1) {
                try {
                    const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
                    if (response && response.data) {
                        setMedicineOptions(response.data);
                    }
                } catch (error) {
                    console.error('Error fetching medicine options:', error);
                }
            } else {
                setMedicineOptions([]);
            }
        };

        const debounceTimer = setTimeout(() => {
            fetchMedicineOptions();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [medicineSearchText]);

    useEffect(() => {
        const frequencyMap = {
            'Once a day': 1,
            'Twice a day': 2,
            'Thrice a day': 3,
            'Four times a day': 4
        };

        let updated = false;
        const newMedicines = medicines.map(med => {
            if (med.frequency && med.duration) {
                const freqValue = frequencyMap[med.frequency] || 0;
                const durationValue = parseInt(med.duration) || 0;
                const calculatedQuantity = freqValue * durationValue;

                if (calculatedQuantity > 0 && med.quantity !== String(calculatedQuantity)) {
                    updated = true;
                    return { ...med, quantity: String(calculatedQuantity) };
                }
            } else if (med.quantity !== '') {
                updated = true;
                return { ...med, quantity: '' };
            }
            return med;
        });

        if (updated) {
            setMedicines(newMedicines);
        }
    }, [medicines]);

    const handleMedicineFieldChange = (index, field, value) => {
        const updated = [...medicines];
        updated[index][field] = value;
        setMedicines(updated);
    };

    const addMedicineRow = () => {
        setMedicines([...medicines, { medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
    };

    const removeMedicineRow = (index) => {
        if (medicines.length === 1) {
            setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
        } else {
            setMedicines(medicines.filter((_, i) => i !== index));
        }
    };

    const [selectedValue, setSelectedValue] = useState('');
    const [savedVisualExam, setSavedVisualExam] = useState(null);

    const [formData, setFormData] = useState({
        assessmentDate: today,
        exam: {
            abnormalPatches: false,
            noAbnormalityFound: false,
            examDate: '',
            cavity: '',
            chronicTrauma: '',
            dentalCaries: null,
            dentures: null,
            lossOfTooth: null,
            fissures: null,
            induration: null,
            lymphadenopathy: null,
            otherFindings: null,
            otherFindingsTxt: '',
            rootCanalHistory: '',
            site: '',
            visualExam: {
                leukoplakiaPatch: [],
                erythroplakiaPatch: [],
                ulcerPatch: [],
                growthPatch: [],
                lockedjawPatch: [],
                subfibrosisPatch: [],
                smokerspalatePatch: [],
                lichenPlanusPatch: []
            }
        }
    });

    const getPatchLabelForDot = (dotValue) => {
        const patchLabels = {
            leukoplakiaPatch: 'Leukoplakia',
            erythroplakiaPatch: 'Erythroplakia',
            ulcerPatch: 'Ulcer',
            growthPatch: 'Growth',
            lockedjawPatch: 'LockedJaw',
            subfibrosisPatch: 'Submucousfibrosis',
            smokerspalatePatch: 'Smokerspalate',
            lichenPlanusPatch: 'LichenPlanus',
        };

        const selectedLabels = [];
        for (const [key, label] of Object.entries(patchLabels)) {
            if (formData.exam?.visualExam?.[key]?.includes(dotValue)) {
                selectedLabels.push(label);
            }
        }
        return selectedLabels.length > 0 ? selectedLabels.join(',') : selectedValue;
    };

    const activeDotsForDisplay = useMemo(() => {
        if (!formData.exam?.visualExam) return [];
        const patchKeyMap = {
            Leukoplakia: 'leukoplakiaPatch',
            Erythroplakia: 'erythroplakiaPatch',
            Ulcer: 'ulcerPatch',
            Growth: 'growthPatch',
            LockedJaw: 'lockedjawPatch',
            Submucousfibrosis: 'subfibrosisPatch',
            Smokerspalate: 'smokerspalatePatch',
            LichenPlanus: 'lichenPlanusPatch',
        };

        if (selectedValue && patchKeyMap[selectedValue]) {
            const patchKey = patchKeyMap[selectedValue];
            return formData.exam.visualExam[patchKey] || [];
        }

        const allDots = [];
        Object.values(formData.exam.visualExam).forEach((list) => {
            if (Array.isArray(list)) {
                list.forEach((dot) => {
                    if (!allDots.includes(dot)) {
                        allDots.push(dot);
                    }
                });
            }
        });
        return allDots;
    }, [formData.exam?.visualExam, selectedValue]);

    const handleDotClickOral = (dotValue, e) => {
        if (formData.exam.noAbnormalityFound) return;
        if (e && e.stopPropagation) e.stopPropagation();

        const patchKeyMap = {
            Leukoplakia: 'leukoplakiaPatch',
            Erythroplakia: 'erythroplakiaPatch',
            Ulcer: 'ulcerPatch',
            Growth: 'growthPatch',
            LockedJaw: 'lockedjawPatch',
            Submucousfibrosis: 'subfibrosisPatch',
            Smokerspalate: 'smokerspalatePatch',
            LichenPlanus: 'lichenPlanusPatch',
        };

        const patchKey = patchKeyMap[selectedValue];
        if (!patchKey) {
            toast.info("Please select a symptom above (e.g. Leukoplakia, Erythroplakia) to mark location.");
            return;
        }

        setFormData((prev) => {
            const currentList = prev.exam?.visualExam?.[patchKey] || [];
            const updatedList = currentList.includes(dotValue)
                ? currentList.filter((v) => v !== dotValue)
                : [...currentList, dotValue];

            const newVisualExam = {
                ...prev.exam.visualExam,
                [patchKey]: updatedList,
            };

            const hasAnySymptom = Object.values(newVisualExam).some(
                (list) => Array.isArray(list) && list.includes(dotValue)
            );

            setActiveDots((prevActive) => {
                if (hasAnySymptom) {
                    return prevActive.includes(dotValue) ? prevActive : [...prevActive, dotValue];
                } else {
                    return prevActive.filter((d) => d !== dotValue);
                }
            });

            return {
                ...prev,
                exam: {
                    ...prev.exam,
                    visualExam: newVisualExam,
                },
            };
        });
    };

    const handleClickOutside = (e) => {
        if (wrapperRef.current && wrapperRef.current.contains(e.target)) {
            return;
        }
        if (savedVisualExam) {
            setFormData((prev) => ({
                ...prev,
                exam: {
                    ...prev.exam,
                    visualExam: JSON.parse(JSON.stringify(savedVisualExam))
                }
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                exam: {
                    ...prev.exam,
                    visualExam: {
                        leukoplakiaPatch: [],
                        erythroplakiaPatch: [],
                        ulcerPatch: [],
                        growthPatch: [],
                        lockedjawPatch: [],
                        subfibrosisPatch: [],
                        smokerspalatePatch: [],
                        lichenPlanusPatch: []
                    }
                }
            }));
            toast.info("Cleared unsaved markings");
        }
    };

    const handleConfirmNo = () => {
        setOpenConfirmDialog(false);
        setFormData(prev => ({
            ...prev,
            exam: {
                ...prev.exam,
                noAbnormalityFound: false,
                abnormalPatches: true
            }
        }));
    };

    const handleConfirmYes = () => {
        setOpenConfirmDialog(false);
        setActiveDots([]);
        setFormData(prev => ({
            ...prev,
            exam: {
                ...prev.exam,
                noAbnormalityFound: true,
                abnormalPatches: false,
                cavity: '',
                chronicTrauma: '',
                dentalCaries: null,
                dentures: null,
                lossOfTooth: null,
                fissures: null,
                induration: null,
                lymphadenopathy: null,
                otherFindings: null,
                otherFindingsTxt: '',
                rootCanalHistory: '',
                site: '',
                visualExam: {
                    leukoplakiaPatch: [],
                    erythroplakiaPatch: [],
                    ulcerPatch: [],
                    growthPatch: [],
                    lockedjawPatch: [],
                    subfibrosisPatch: [],
                    smokerspalatePatch: [],
                    lichenPlanusPatch: []
                }
            }
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked, dataset } = e.target;

        const parseValue = () => {
            if (type === 'radio' && (value === 'Yes' || value === 'No')) {
                return value === 'Yes';
            }
            return type === 'checkbox' ? checked : value;
        };

        const parsedValue = parseValue();
        const examKeys = [
            "cavity", "chronicTrauma", "dentalCaries", "dentures", "lossOfTooth",
            "fissures", "induration", "lymphadenopathy", "otherFindings",
            "otherFindingsTxt", "rootCanalHistory", "site", "abnormalPatches", "noAbnormalityFound"
        ];

        if (dataset?.parent === 'exam' || examKeys.includes(name)) {
            setFormData((prev) => {
                const isNoAbnormality = name === 'noAbnormalityFound' ? parsedValue : prev.exam.noAbnormalityFound;
                return {
                    ...prev,
                    exam: {
                        ...prev.exam,
                        [name]: parsedValue,
                        abnormalPatches: !isNoAbnormality
                    },
                };
            });
        } else if (dataset?.parent === 'visualExam') {
            setFormData((prev) => {
                const currentList = prev.exam.visualExam[name] || [];
                return {
                    ...prev,
                    exam: {
                        ...prev.exam,
                        visualExam: {
                            ...prev.exam.visualExam,
                            [name]: checked
                                ? [...currentList, value]
                                : currentList.filter((item) => item !== value),
                        },
                    },
                };
            });
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: parsedValue,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            exam: {
                abnormalPatches: formData.exam.abnormalPatches ?? false,
                noAbnormalityFound: formData.exam.noAbnormalityFound ?? false,
                examDate: formatToApiDate(formData.assessmentDate),
                cavity: formData.exam.cavity || null,
                chronicTrauma: formData.exam.chronicTrauma || null,
                dentalCaries: formData.exam.dentalCaries ?? null,
                dentures: formData.exam.dentures ?? null,
                lossOfTooth: formData.exam.lossOfTooth ?? null,
                fissures: formData.exam.fissures ?? null,
                induration: formData.exam.induration ?? null,
                lymphadenopathy: formData.exam.lymphadenopathy ?? null,
                otherFindings: formData.exam.otherFindings ?? null,
                otherFindingsTxt: formData.exam.otherFindingsTxt || null,
                rootCanalHistory: formData.exam.rootCanalHistory || null,
                site: formData.exam.site || null,
                visualExam: {
                    leukoplakiaPatch: formData.exam.visualExam.leukoplakiaPatch || [],
                    erythroplakiaPatch: formData.exam.visualExam.erythroplakiaPatch || [],
                    ulcerPatch: formData.exam.visualExam.ulcerPatch || [],
                    growthPatch: formData.exam.visualExam.growthPatch || [],
                    lockedjawPatch: formData.exam.visualExam.lockedjawPatch || [],
                    subfibrosisPatch: formData.exam.visualExam.subfibrosisPatch || [],
                    smokerspalatePatch: formData.exam.visualExam.smokerspalatePatch || [],
                    lichenPlanusPatch: formData.exam.visualExam.lichenPlanusPatch || []
                }
            }
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitOralScreening(individualId, oralScreeningId, payload);

            if (response.success) {
                toast.success(`Oral cancer screening data has saved successfully`);
                setSavedVisualExam(JSON.parse(JSON.stringify(formData.exam.visualExam)));

                const newEncounterId = response.data?.encounterId;

                if (newEncounterId) {
                    setEncounterId(newEncounterId);
                }
                setActiveWorkflowTab(1);
                setIsPageLoading(false);
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
                setIsPageLoading(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setIsPageLoading(false);
        }
    };

    const handleSaveFollowup = async () => {
        if (!validateFollowupForm()) {
            toast.error("Please fill in the mandatory fields correctly.");
            return;
        }

        const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const toFacility = Number(followupTo) || FACILITY_MAP[followupTo] || fromFacility;

        const followUpPayload = {
            followUpDueDate: dueDate ? (dayjs(dueDate, 'DD-MM-YYYY', true).isValid() ? dueDate : dayjs(dueDate).format('DD-MM-YYYY')) : '',
            followUpDuration: `${durationDays || 0};${durationWeeks || 0};${durationMonths || 0}`,
            followUpReason: followupReason,
            fromFacility: fromFacility,
            toFacility: toFacility,
            type: "E",
            fromUser: loggedData.username || localStorage.getItem("ncd_username") || "doc_1220004",
            encounterId: encounterId || oralData?.data?.exam?.encounterId || oralData?.data?.encounterId || oralData?.encounterId || null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitOralFollowup(individualId, oralScreeningId, followUpPayload);
            if (response && response.success) {
                toast.success(response.message || "Follow-up details saved successfully!");
                try {
                    const historyResponse = await ncdAPI.getScreeningFollowup(100, individualId, oralScreeningId);
                    if (historyResponse) {
                        setFollowupHistory(historyResponse.data || (Array.isArray(historyResponse) ? historyResponse : []));
                    }
                } catch (error) {
                    console.error("Failed to fetch updated follow-up history:", error);
                }
                setActiveWorkflowTab(0);
                setFollowupReason("");
                setFollowupTo("");
                setDurationDays("0");
                setDurationWeeks("0");
                setDurationMonths("0");
                setDueDate("");
                setErrors({});
            } else {
                toast.error(response?.message || "Failed to save follow-up details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitOralFollowup error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving follow-up details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSaveReferral = async () => {
        if (!validateReferralForm()) {
            toast.error("Please fill in the mandatory Referral fields (*)");
            return;
        }

        const referredFromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const referredToFacility = referTo ? (FACILITY_MAP[referTo] || String(referTo)) : "3400";

        const payload = {
            referredDate: referDate ? dayjs(referDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : '',
            referredToFacility: referredToFacility,
            referredFromFacility: referredFromFacility,
            referralReason: referReason,
            referredSpeciality: referredSpecialty,
            privateHospitalCare: seekingPrivateCare === "Yes",
            provisionalDiagnosis: provisionalDiagnosis || null,
            encounterId: null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitOralRefer(individualId, oralScreeningId, payload);
            if (response && response.success) {
                toast.success(response.message || "Referral details saved successfully!");
                setActiveWorkflowTab(0);
                setReferTo("");
                setReferReason("");
                setProvisionalDiagnosis("");
                setReferredSpecialty("");
                setSeekingPrivateCare("");
                setErrors({});
            } else {
                toast.error(response?.message || "Failed to save referral details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitOralRefer error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving referral details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSaveLesionStatus = () => {
        setIsRegressionModalOpen(false);
        if (lesionStatus === 'regression') {
            setRegressionObserved(true);
            setDiagnosisAction("Management");
            setActionTakenTitle("Action to be taken");
            setActionTakenText("Advise individual on risk factors. Tobacco chewing is the single most important risk factor for oral cancer. Other risk factors include alcohol use, betel nut chewing, and chronic trauma to oral mucosa by sharp tooth or ill-fitting dentures. Over a period of time, malignancy may develop in these lesions.");
            setIsActionAlertDialogOpen(true);
        } else {
            setRegressionObserved(false);
            setDiagnosisAction("Refer");
            setActionTakenTitle("Action to be taken");
            setActionTakenText("Lesion is unchanged or has progressed. A detailed intraoral examination or biopsy is recommended. Recommended action is to refer to CHC/DH.");
            setIsActionAlertDialogOpen(true);
        }
    };

    const executeSubmitDiagnosis = async (payload) => {
        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitScreeningDiagnosis(100, individualId, oralScreeningId, payload);
            if (response && response.success) {
                toast.success("Diagnosis action updated successfully!");
                setIsDiagnosisSaved(true);
                if (isDhOrChc && (dhDiagnosisData.malignantUlcer === "Yes" || dhDiagnosisData.malignantGrowth === "Yes") && dhDiagnosisData.typeOfDiagnosis === "Confirmed") {
                    setIsCancerConfirmedLocked(true);
                }
                setActiveWorkflowTab(2);
            } else {
                toast.error(response?.message || "Failed to save diagnosis details");
            }
        } catch (error) {
            console.error("submitScreeningDiagnosis error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving diagnosis details");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleConfirmCancerSave = async () => {
        setIsCancerConfirmDialogOpen(false);
        if (pendingDiagnosisPayload) {
            await executeSubmitDiagnosis(pendingDiagnosisPayload);
            setPendingDiagnosisPayload(null);
        }
    };

    const handleSaveDiagnosis = async () => {
        let payload;
        if (isDhOrChc) {
            if (!dhDiagnosisData.noAbnormalityDetected) {
                if (!dhDiagnosisData.typeOfDiagnosis) {
                    setDhErrors({ typeOfDiagnosis: "This field is required." });
                    toast.error("Please fill in mandatory fields (*)");
                    return;
                }

                const hasAnyFieldSelected = Boolean(
                    dhDiagnosisData.normalVariation ||
                    dhDiagnosisData.frictionalKeratosis ||
                    dhDiagnosisData.tobaccoPouchLesion ||
                    dhDiagnosisData.smokersPalate ||
                    dhDiagnosisData.oralLichenoidReaction ||
                    dhDiagnosisData.proliferativeVerrucousLeukoplakia ||
                    dhDiagnosisData.oralLichenPlanus ||
                    dhDiagnosisData.oralSubmucousFibrosis ||
                    dhDiagnosisData.oralLeukoplakia ||
                    dhDiagnosisData.oralCandidiasis ||
                    dhDiagnosisData.benignUlcer ||
                    dhDiagnosisData.benignGrowth ||
                    dhDiagnosisData.malignantUlcer ||
                    dhDiagnosisData.malignantGrowth ||
                    dhDiagnosisData.anyOther
                );

                if (!hasAnyFieldSelected) {
                    toast.error("Please select at least one diagnosis field.");
                    return;
                }
            }

            const getRadioBool = (val) => {
                if (dhDiagnosisData.noAbnormalityDetected) return null;
                if (val === "Yes") return true;
                if (val === "No") return false;
                return null;
            };

            const getRemarks = (val) => {
                if (dhDiagnosisData.noAbnormalityDetected) return "";
                return val || "";
            };

            const isNAD = dhDiagnosisData.noAbnormalityDetected;

            payload = {
                assessmentDate: formatToApiDate(formData.assessmentDate),
                optedToReview: null,
                optedToRefer: null,
                optedForManagement: null,
                diagnosis: {
                    diagnosisDate: formatToApiDate(formData.assessmentDate),
                    isNAD: isNAD ? true : null,
                    provisionalDiagnosis: isNAD ? false : (dhDiagnosisData.typeOfDiagnosis === "Provisional"),
                    normalVariation: getRadioBool(dhDiagnosisData.normalVariation),
                    normalVariationRemarks: getRemarks(dhDiagnosisData.normalVariationRemarks),
                    keratosis: getRadioBool(dhDiagnosisData.frictionalKeratosis),
                    keratosisRemarks: getRemarks(dhDiagnosisData.frictionalKeratosisRemarks),
                    tobaccoPouchLesion: getRadioBool(dhDiagnosisData.tobaccoPouchLesion),
                    tobaccoPouchLesionRemarks: getRemarks(dhDiagnosisData.tobaccoPouchLesionRemarks),
                    smokersPalate: getRadioBool(dhDiagnosisData.smokersPalate),
                    smokersPalateRemarks: getRemarks(dhDiagnosisData.smokersPalateRemarks),
                    lichenoidReaction: getRadioBool(dhDiagnosisData.oralLichenoidReaction),
                    lichenoidReactionRemarks: getRemarks(dhDiagnosisData.oralLichenoidReactionRemarks),
                    proliferativeLeukoplakia: getRadioBool(dhDiagnosisData.proliferativeVerrucousLeukoplakia),
                    proliferativeLeukoplakiaRemarks: getRemarks(dhDiagnosisData.proliferativeVerrucousLeukoplakiaRemarks),
                    lichenPlanus: isNAD ? null : (dhDiagnosisData.oralLichenPlanus || null),
                    submucousFibrosis: isNAD ? null : (dhDiagnosisData.oralSubmucousFibrosis || null),
                    leukoplakia: isNAD ? null : (dhDiagnosisData.oralLeukoplakia || null),
                    candidiasis: isNAD ? null : (dhDiagnosisData.oralCandidiasis || null),
                    benignUlcer: isNAD ? null : (dhDiagnosisData.benignUlcer || null),
                    benignGrowth: isNAD ? null : (dhDiagnosisData.benignGrowth || null),
                    malignantUlcer: getRadioBool(dhDiagnosisData.malignantUlcer),
                    malignantUlcerRemarks: getRemarks(dhDiagnosisData.malignantUlcerRemarks),
                    malignantGrowth: getRadioBool(dhDiagnosisData.malignantGrowth),
                    malignantGrowthRemarks: getRemarks(dhDiagnosisData.malignantGrowthRemarks),
                    other: getRadioBool(dhDiagnosisData.anyOther),
                    otherRemarks: getRemarks(dhDiagnosisData.anyOtherRemarks)
                },
                docObservations: doctorsObservation || null,
                encounterId: encounterId || oralData?.data?.encounterId || oralData?.data?.data?.encounterId || oralData?.encounterId || oralData?.data?.exam?.encounterId || null
            };

            const isCancerIndicated = (dhDiagnosisData.malignantUlcer === "Yes" || dhDiagnosisData.malignantGrowth === "Yes") && dhDiagnosisData.typeOfDiagnosis === "Confirmed";
            if (isCancerIndicated && !isCancerConfirmedLocked) {
                setPendingDiagnosisPayload(payload);
                setIsCancerConfirmDialogOpen(true);
                return;
            }
        } else {
            const isNAD = diagnosisAction === "NAD";
            payload = {
                assessmentDate: formatToApiDate(formData.assessmentDate),
                optedToReview: diagnosisAction === "Review" || diagnosisAction === "FollowUp" ? true : null,
                optedToRefer: diagnosisAction === "Refer" ? true : null,
                optedForManagement: diagnosisAction === "Management" ? true : null,
                regressionObserved: regressionObserved ?? null,
                diagnosis: isNAD ? {
                    diagnosisDate: formatToApiDate(formData.assessmentDate),
                    isNAD: true,
                    provisionalDiagnosis: false
                } : null,
                docObservations: doctorsObservation || null,
                encounterId: encounterId || oralData?.data?.encounterId || oralData?.data?.data?.encounterId || oralData?.encounterId || oralData?.data?.exam?.encounterId || null
            };
        }

        await executeSubmitDiagnosis(payload);
    };

    const handleSaveReview = async (customPayload) => {
        const activeEncounterId = encounterId || oralData?.data?.exam?.encounterId || oralData?.data?.encounterId || oralData?.encounterId || null;

        if (isDhOrChc && customPayload) {
            if (!customPayload.encounterId) {
                customPayload.encounterId = activeEncounterId;
            }
            try {
                setIsPageLoading(true);
                const response = await ncdAPI.submitScreeningTreatment(100, individualId, oralScreeningId, customPayload);
                if (response && response.success) {
                    toast.success(response.message || "Review details saved successfully!");
                    setActiveWorkflowTab(0);
                } else {
                    toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
                }
            } catch (error) {
                console.error("submitScreeningTreatment review error:", error);
                toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
            } finally {
                setIsPageLoading(false);
            }
            return;
        }

        if (referTo || referReason) {
            if (!referTo) {
                toast.error("Please select Refer to facility");
                return;
            }
            if (!referReason) {
                toast.error("Please select Reason for Referral");
                return;
            }
        }

        const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3200;
        const referredToFacility = Number(referTo) || FACILITY_MAP[referTo] || 3400;

        const referPayload = (referTo || referReason) ? {
            referredDate: dayjs(formData.assessmentDate).format('DD-MM-YYYY'),
            referredToFacility: referredToFacility,
            referredFromFacility: fromFacility,
            referralReason: referReason,
            referredSpeciality: referredSpecialty || "Medical",
            privateHospitalCare: seekingPrivateCare === "Yes",
            provisionalDiagnosis: reviewObservations || referralRemarks || null,
            encounterId: activeEncounterId
        } : null;

        const payload = {
            assessmentDate: formatToApiDate(formData.assessmentDate),
            encounterId: activeEncounterId,
            treatment: null,
            refer: referPayload,
            followUp: null,
            reviewComments: reviewObservations || null,
            medicines: medicines.filter(med => med.medicine).length > 0 ? medicines.filter(med => med.medicine).map(med => ({
                conditn: "Oral Cancer",
                name: med.medicine,
                prscrbdStrngth: null,
                freq: med.frequency,
                quan: Number(med.quantity) || null,
                dur: Number(med.duration) || null,
                durType: null,
                spInst: med.instruction
            })) : null
        };

        try {
            setIsPageLoading(true);
            const response = await ncdAPI.submitScreeningTreatment(100, individualId, oralScreeningId, payload);
            if (response && response.success) {
                toast.success(response.message || "Review details saved successfully!");
                setActiveWorkflowTab(0);
                setReferTo("");
                setReferReason("");
                setReferralRemarks("");
                setReviewObservations("");
                setMedicines([{ medicine: '', frequency: '', duration: '', quantity: '', instruction: '' }]);
            } else {
                toast.error(response?.message || "Failed to save review details", { className: "custom-toast-error" });
            }
        } catch (error) {
            console.error("submitScreeningTreatment review error:", error);
            toast.error(error.response?.data?.message || "Something went wrong while saving review details", { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    return (
        <>
            {isPageLoading && <Loader />}

            {/* Top Tab Switchers */}
            <Box sx={{ borderBottom: '1px solid #cbd5e1', bgcolor: '#f1f5f9', px: 2, display: 'flex', gap: 4, mb: 1 }}>
                {[
                    { id: 0, label: "Examination" },
                    { id: 1, label: "Diagnosis" },
                    { id: 2, label: "Review / Follow Up" },
                    ...(activeWorkflowTab === 3 ? [{ id: 3, label: "Follow Up" }] : []),
                    ...(activeWorkflowTab === 4 ? [{ id: 4, label: "Refer" }] : [])
                ].map((tab) => (
                    <Box
                        key={tab.id}
                        onClick={() => {
                            if (tab.id === 0) {
                                setActiveWorkflowTab(0);
                            } else if (tab.id === 1) {
                                const hasEncounter = encounterId || oralData?.encounterId || oralData?.data?.encounterId;
                                if (hasEncounter) {
                                    setActiveWorkflowTab(1);
                                } else {
                                    toast.error("Please submit the Examination details first.");
                                }
                            } else if (tab.id === 2) {
                                const hasEncounter = encounterId || oralData?.encounterId || oralData?.data?.encounterId;
                                const hasDiag = isDiagnosisSaved || oralData?.optedToReview || oralData?.optedToRefer || oralData?.optedForManagement || oralData?.data?.optedToReview || oralData?.data?.optedToRefer || oralData?.data?.optedForManagement;
                                if (!hasEncounter) {
                                    toast.error("Please submit the Examination details first.");
                                } else if (!hasDiag) {
                                    toast.error("Please submit the Diagnosis details first.");
                                } else {
                                    setActiveWorkflowTab(2);
                                }
                            } else {
                                setActiveWorkflowTab(tab.id);
                            }
                        }}
                        sx={{
                            py: 1.8,
                            px: 1,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: activeWorkflowTab === tab.id ? '#3f51b5' : '#64748b',
                            borderBottom: activeWorkflowTab === tab.id ? '3px solid #3f51b5' : '3px solid transparent',
                            transition: 'all 0.2s',
                            '&:hover': { color: '#3f51b5' }
                        }}
                    >
                        {tab.label}
                    </Box>
                ))}
            </Box>

            {/* Render Modular Tab Subcomponents */}
            {activeWorkflowTab === 0 && (
                <OralExaminationTab
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    activeDots={activeDotsForDisplay}
                    wrapperRef={wrapperRef}
                    dots={dots}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                    handleDotClickOral={handleDotClickOral}
                    getPatchLabelForDot={getPatchLabelForDot}
                    handleClickOutside={handleClickOutside}
                    screeningStatus={screeningStatus}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                    setOpenConfirmDialog={setOpenConfirmDialog}
                    today={today}
                    isDhOrChc={isDhOrChc}
                />
            )}

            {activeWorkflowTab === 1 && (
                <OralDiagnosisTab
                    facilityType={facilityType}
                    isDhOrChc={isDhOrChc}
                    formData={formData}
                    diagnosisAction={diagnosisAction}
                    setDiagnosisAction={setDiagnosisAction}
                    doctorsObservation={doctorsObservation}
                    setDoctorsObservation={setDoctorsObservation}
                    dhDiagnosisData={dhDiagnosisData}
                    setDhDiagnosisData={setDhDiagnosisData}
                    dhErrors={dhErrors}
                    setDhErrors={setDhErrors}
                    setIsNadModalOpen={setIsNadModalOpen}
                    handleSaveDiagnosis={handleSaveDiagnosis}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                    screeningStatus={screeningStatus}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    isDiagnosed={isDiagnosed}
                    patientDiagnosis={patientDiagnosis}
                    isCancerConfirmedLocked={isCancerConfirmedLocked}
                    isNad={isNad}
                    referralRecommended={referralRecommended}
                    managementRecommended={managementRecommended}
                />
            )}

            {activeWorkflowTab === 2 && (
                <OralReviewTab
                    facilityType={facilityType}
                    isDhOrChc={isDhOrChc}
                    individualId={individualId}
                    oralScreeningId={oralScreeningId}
                    conditionId={100}
                    screeningStatus={screeningStatus}
                    formData={formData}
                    reviewObservations={reviewObservations}
                    setReviewObservations={setReviewObservations}
                    treatmentType={treatmentType}
                    setTreatmentType={setTreatmentType}
                    treatmentOther={treatmentOther}
                    setTreatmentOther={setTreatmentOther}
                    medicines={medicines}
                    medicineOptions={medicineOptions}
                    setMedicineSearchText={setMedicineSearchText}
                    handleMedicineFieldChange={handleMedicineFieldChange}
                    addMedicineRow={addMedicineRow}
                    removeMedicineRow={removeMedicineRow}
                    followupReason={followupReason}
                    setFollowupReason={setFollowupReason}
                    followupTo={followupTo}
                    setFollowupTo={setFollowupTo}
                    durationDays={durationDays}
                    durationWeeks={durationWeeks}
                    durationMonths={durationMonths}
                    handleDurationChange={handleDurationChange}
                    dueDate={dueDate}
                    setDueDate={setDueDate}
                    referTo={referTo}
                    setReferTo={setReferTo}
                    referReason={referReason}
                    setReferReason={setReferReason}
                    referralRemarks={referralRemarks}
                    setReferralRemarks={setReferralRemarks}
                    handleSaveReview={handleSaveReview}
                    setActiveWorkflowTab={setActiveWorkflowTab}
                />
            )}

            {activeWorkflowTab === 3 && (
                <OralFollowupTab
                    followupReason={followupReason}
                    setFollowupReason={setFollowupReason}
                    followupTo={followupTo}
                    setFollowupTo={setFollowupTo}
                    durationDays={durationDays}
                    durationWeeks={durationWeeks}
                    durationMonths={durationMonths}
                    handleDurationChange={handleDurationChange}
                    dueDate={dueDate}
                    errors={errors}
                    setErrors={setErrors}
                    followupHistory={followupHistory}
                    getFacilityLabel={getFacilityLabel}
                    handleSaveFollowup={handleSaveFollowup}
                    facilityType={loggedData.facilityType}
                />
            )}

            {activeWorkflowTab === 4 && (
                <OralReferTab
                    referDate={referDate}
                    setReferDate={setReferDate}
                    referTo={referTo}
                    setReferTo={setReferTo}
                    referReason={referReason}
                    setReferReason={setReferReason}
                    provisionalDiagnosis={provisionalDiagnosis}
                    setProvisionalDiagnosis={setProvisionalDiagnosis}
                    referredSpecialty={referredSpecialty}
                    setReferredSpecialty={setReferredSpecialty}
                    seekingPrivateCare={seekingPrivateCare}
                    setSeekingPrivateCare={setSeekingPrivateCare}
                    errors={errors}
                    setErrors={setErrors}
                    handleSaveReferral={handleSaveReferral}
                />
            )}

            {/* Status Update Modal */}
            <Dialog
                open={isStatusModalOpen}
                onClose={() => setIsStatusModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 1,
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, fontSize: '18px', color: '#1e293b', pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" sx={{ fontSize: '20px', color: '#f97316' }}>⚡</Typography>
                        <Typography component="span" sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
                            Update Screening Status
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setIsStatusModalOpen(false)} size="small" sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' } }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ py: 3, px: 3 }}>
                    <Grid container spacing={3}>
                        {/* Date Field */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#334155' }}>
                                        Date <span style={{ color: '#ef4444' }}>*</span>
                                    </Typography>
                                    <Tooltip title="Specify consultation date" arrow placement="top">
                                        <IconButton size="small" sx={{ p: 0.2, color: '#3f51b5' }}>
                                            <InfoIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        value={modalDate ? dayjs(modalDate, 'YYYY-MM-DD') : null}
                                        onChange={(newValue) => {
                                            setModalDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                                            if (modalErrors.date) setModalErrors(prev => ({ ...prev, date: null }));
                                        }}
                                        maxDate={dayjs()}
                                        disableFuture
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: !!modalErrors.date,
                                                helperText: modalErrors.date,
                                                sx: {
                                                    bgcolor: '#fff',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        '& fieldset': { borderColor: '#cbd5e1' }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        </Grid>

                        {/* Status Field */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#334155' }}>
                                    Update status to <span style={{ color: '#ef4444' }}>*</span>
                                </Typography>
                                <FormControl fullWidth size="small" error={!!modalErrors.status}>
                                    <Select
                                        value={modalStatus}
                                        onChange={(e) => {
                                            setModalStatus(e.target.value);
                                            if (modalErrors.status) setModalErrors(prev => ({ ...prev, status: null }));
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '6px',
                                            bgcolor: '#fff',
                                            '& fieldset': { borderColor: '#cbd5e1' }
                                        }}
                                    >
                                        <MenuItem value="" disabled>Select</MenuItem>
                                        {statusOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {modalErrors.status && (
                                        <FormHelperText error>{modalErrors.status}</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>

                        {/* Facility Field */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#334155' }}>
                                    Facility <span style={{ color: '#ef4444' }}>*</span>
                                </Typography>
                                <FormControl fullWidth size="small" error={!!modalErrors.facility}>
                                    <Select
                                        value={modalFacility}
                                        onChange={(e) => {
                                            setModalFacility(e.target.value);
                                            if (modalErrors.facility) setModalErrors(prev => ({ ...prev, facility: null }));
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '6px',
                                            bgcolor: '#fff',
                                            '& fieldset': { borderColor: '#cbd5e1' }
                                        }}
                                    >
                                        <MenuItem value="" disabled>Select Facility</MenuItem>
                                        {facilityOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {modalErrors.facility && (
                                        <FormHelperText error>{modalErrors.facility}</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Grid>

                        {/* Verified Card */}
                        <Grid item xs={12} sm={6}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    px: 2,
                                    borderRadius: '10px',
                                    bgcolor: modalVerified ? 'rgba(59, 130, 246, 0.04)' : '#f8fafc',
                                    border: '1px dashed',
                                    borderColor: modalVerified ? '#3f51b5' : '#cbd5e1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    minHeight: '62px'
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>
                                        Verified
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                        Mark this status check as verified
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={modalVerified}
                                    onChange={(e) => setModalVerified(e.target.checked)}
                                    sx={{ p: 0.5 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', gap: 1.5, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setIsStatusModalOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#475569',
                            borderColor: '#cbd5e1',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            px: 3,
                            py: 0.8,
                            borderRadius: '8px',
                            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                        }}
                    >
                        CANCEL
                    </Button>
                    <Button
                        onClick={handleSaveModalStatus}
                        variant="contained"
                        sx={{
                            bgcolor: '#3548ae',
                            color: '#fff',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            px: 3.5,
                            py: 0.8,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(53, 72, 174, 0.3)',
                            '&:hover': { bgcolor: '#2b3674' }
                        }}
                    >
                        SAVE
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirmDialog}
                onClose={handleConfirmNo}
                PaperProps={{
                    sx: {
                        padding: '16px',
                        borderRadius: '8px',
                        maxWidth: '450px',
                        width: '100%',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', p: 1, pb: 2 }}>Confirm</DialogTitle>
                <DialogContent sx={{ p: 1, pb: 2 }}>
                    <Typography sx={{ fontSize: '15px', color: '#333', lineHeight: 1.5 }}>
                        Abnormal findings, if any, will be cleared and the patient will be marked NAD. Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 1, justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                        onClick={handleConfirmNo}
                        variant="contained"
                        sx={{
                            backgroundColor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 'bold',
                            minWidth: '80px',
                            '&:hover': {
                                backgroundColor: '#303f9f',
                            }
                        }}
                    >
                        NO
                    </Button>
                    <Button
                        onClick={handleConfirmYes}
                        variant="contained"
                        sx={{
                            backgroundColor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 'bold',
                            minWidth: '80px',
                            '&:hover': {
                                backgroundColor: '#303f9f',
                            }
                        }}
                    >
                        YES
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action to be taken Dialog */}
            <Dialog
                open={isNadModalOpen}
                onClose={() => setIsNadModalOpen(false)}
                PaperProps={{
                    sx: {
                        padding: '16px',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '100%',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', p: 1, pb: 2, color: '#1a202c' }}>
                    Action to be taken
                </DialogTitle>
                <DialogContent sx={{ p: 1, pb: 3 }}>
                    <Typography sx={{ fontSize: '15px', color: '#334155', lineHeight: 1.5 }}>
                        Result indicates NAD (No abnormality Detected). Recommended action is to ask individual to return for routine screening.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 1, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setIsNadModalOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: '#3f51b5',
                            color: '#fff',
                            fontWeight: 700,
                            px: 3,
                            py: 0.8,
                            borderRadius: '4px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#303f9f',
                            }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lesion Status / Regression Check Dialog (PHC Mode) */}
            <Dialog
                open={isRegressionModalOpen}
                onClose={() => setIsRegressionModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
                    Lesion Status Check
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#334155', mb: 2 }}>
                        Please choose an action below to indicate the status of the lesions
                    </Typography>
                    <RadioGroup
                        value={lesionStatus}
                        onChange={(e) => setLesionStatus(e.target.value)}
                    >
                        <FormControlLabel value="regression" control={<Radio />} label={<Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Regression</Typography>} />
                        <FormControlLabel value="progression" control={<Radio />} label={<Typography sx={{ fontWeight: 600, fontSize: '14px' }}>No Change / Progression</Typography>} />
                    </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setIsRegressionModalOpen(false)} sx={{ color: '#3f51b5', fontWeight: 700 }}>
                        CANCEL
                    </Button>
                    <Button onClick={handleSaveLesionStatus} variant="contained" sx={{ bgcolor: '#3f51b5', fontWeight: 700 }}>
                        SAVE
                    </Button>
                </DialogActions>
            </Dialog>

            {/* General Action Alert Dialog */}
            <Dialog
                open={isActionAlertDialogOpen}
                onClose={() => setIsActionAlertDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
                    {actionTakenTitle}
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.6 }}>
                        {actionTakenText}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsActionAlertDialogOpen(false)} variant="contained" sx={{ bgcolor: '#3f51b5', fontWeight: 700 }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancer Confirmation Dialog */}
            <Dialog
                open={isCancerConfirmDialogOpen}
                onClose={() => setIsCancerConfirmDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b' }}>
                    Confirm
                </DialogTitle>
                <DialogContent sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: '#334155', lineHeight: 1.6 }}>
                        Are you sure you want to diagnose the individual with Oral Cancer?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setIsCancerConfirmDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 700 }}>
                        NO
                    </Button>
                    <Button onClick={handleConfirmCancerSave} variant="contained" sx={{ bgcolor: '#3f51b5', fontWeight: 700 }}>
                        YES
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default OralScreening;

